import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus } from "lucide-react";
import { useOrganizationScopedDeviceGroups, useDeviceGroupsForOrg, DeviceGroup } from "@/hooks/use-device-groups";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { useUserOrganization } from "@/hooks/use-user-organization";
import { CreateGroupDialog } from "./CreateGroupDialog";

interface GroupSelectorProps {
  value: string | null;
  onChange: (groupId: string | null, groupName: string | null) => void;
  organizationId?: string | null;
  deviceOrganizationId?: string | null; // The organization the device belongs to
  disabled?: boolean;
  showCreateButton?: boolean;
}

export const GroupSelector = ({
  value,
  onChange,
  organizationId,
  deviceOrganizationId,
  disabled = false,
  showCreateButton = true,
}: GroupSelectorProps) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { role } = useSimpleRole();
  const { data: userOrgId } = useUserOrganization();
  const isSuperuser = role === 'superuser';
  
  // When the camera's organisation is known, show only that organisation's groups.
  // Otherwise fall back to the user's own organisation (+ sub-organisations).
  const orgGroups = useDeviceGroupsForOrg(deviceOrganizationId);
  const scopedGroups = useOrganizationScopedDeviceGroups();
  const source = deviceOrganizationId ? orgGroups : scopedGroups;
  const groups = source.data ?? [];
  const isLoading = source.isLoading;

  // New groups belong to the camera's organisation
  const effectiveOrgId = deviceOrganizationId ?? organizationId ?? (isSuperuser ? null : userOrgId);

  // Only superusers and admins create groups (editors only choose).
  const canCreateGroupForDevice = (isSuperuser || role === 'admin') && !!effectiveOrgId;

  const handleGroupCreated = (newGroup: DeviceGroup) => {
    // Automatically select the newly created group
    onChange(newGroup.id, newGroup.name);
  };

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === "__create__") {
      setCreateDialogOpen(true);
      return;
    }

    if (selectedValue === "__none__") {
      onChange(null, null);
      return;
    }

    const selectedGroup = groups.find((g) => g.id === selectedValue);
    if (selectedGroup) {
      onChange(selectedGroup.id, selectedGroup.name);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 h-9">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Loading groups...</span>
      </div>
    );
  }

  return (
    <>
      <Select
        value={value || "__none__"}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <SelectTrigger className="h-9">
          <SelectValue placeholder="Select a group">
            {value ? (
              <div className="flex items-center gap-2">
                {groups.find((g) => g.id === value)?.color && (
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: groups.find((g) => g.id === value)?.color || undefined,
                    }}
                  />
                )}
                <span>{groups.find((g) => g.id === value)?.name || "Select a group"}</span>
              </div>
            ) : (
              "No group assigned"
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">
            <span className="text-muted-foreground">No group assigned</span>
          </SelectItem>

          {groups.map((group) => (
            <SelectItem key={group.id} value={group.id}>
              <div className="flex items-center gap-2">
                {group.color && (
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: group.color }}
                  />
                )}
                <span>{group.name}</span>
                {group.reference && (
                  <span className="text-xs text-muted-foreground">
                    ({group.reference})
                  </span>
                )}
              </div>
            </SelectItem>
          ))}

          {showCreateButton && canCreateGroupForDevice && (
            <SelectItem value="__create__" className="text-primary">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                <span>Create New Group</span>
              </div>
            </SelectItem>
          )}

        </SelectContent>
      </Select>

      <CreateGroupDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        organizationId={effectiveOrgId}
        onGroupCreated={handleGroupCreated}
      />
    </>
  );
};
